import React, { useRef, useState, useEffect, useCallback } from "react";

const useLongPress = (
  onLongPress,
  { shouldPreventDefault = true, delay = 300 } = {}
) => {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeoutRef = useRef();
  const targetRef = useRef();

  const start = useCallback(
    (event) => {
      if (shouldPreventDefault && event.target) {
        // Prevent scrolling while holding down
        event.target.addEventListener("touchend", preventDefault);
        targetRef.current = event.target;
      }

      timeoutRef.current = setTimeout(() => {
        onLongPress(event);
        setLongPressTriggered(true);
      }, delay);
    },
    [onLongPress, delay, shouldPreventDefault]
  );

  const clear = useCallback(
    (event, shouldTriggerClick = true) => {
      // Clear the timeout whether the long press was triggered or not
      clearTimeout(timeoutRef.current);

      if (longPressTriggered) {
        // If long press occurred, prevent the click handler from firing
        event.preventDefault();
      }

      setLongPressTriggered(false);

      if (shouldPreventDefault && targetRef.current) {
        targetRef.current.removeEventListener("touchend", preventDefault);
      }
    },
    [longPressTriggered, shouldPreventDefault]
  );

  // Clean up the timer when the component unmounts
  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const preventDefault = (e) => {
    // Optional: Prevents the browser context menu on long press
    if (e.touches.length < 2 && longPressTriggered) {
      e.preventDefault();
    }
  };

  return {
    // The events to spread onto the element
    onMouseDown: start,
    onTouchStart: start,
    onMouseUp: clear,
    onTouchEnd: clear,
    // onTouchCancel is crucial for mobile context switches/interruptions
    onTouchCancel: clear,
  };
};

export default useLongPress;
